"use client";

import { FC, ReactNode } from "react";
import { Box, Collapsible, Flex, Stack, Text } from "@chakra-ui/react";
import { ChevronRightIcon } from "lucide-react";
import { useOptionalLocalStorage } from "@zerotofinals/utils";
import Checkbox from "./Checkbox";
import Dot from "./Dot";

const rootColor = "color-mix(in srgb, var(--chakra-colors-link), black 80%)";

type CheckedState = boolean | "indeterminate";

type Category = {
  id: string;
  title: string;
  subcategories?: Category[];
};

export type Props = {
  storageKey?: {
    expandedRootCategoryIds?: string;
  };
  categories: Category[];
  checkedCategoryIds: string[];
  disabledCategoryIds?: string[];
  disableAllCategories?: boolean;
  noCategoriesMessage?: string;
  onlyAllowLeafNodeSelection?: boolean;
  /** Added as a way of reducing dot count at the subcategory level. Defaults true. */
  supportsSubSubcategorySelection?: boolean;
  setCheckedIds: (checkedIds: string[]) => void;
  renderRootCategoryLabel?: (category: Category) => ReactNode;
  renderSubcategoryLabel?: (category: Category) => ReactNode;
};

const CategorySelection: FC<Props> = ({
  storageKey,
  categories,
  checkedCategoryIds,
  disabledCategoryIds = [],
  disableAllCategories,
  noCategoriesMessage,
  onlyAllowLeafNodeSelection,
  supportsSubSubcategorySelection = true,
  setCheckedIds,
  renderRootCategoryLabel = ({ title }) => <b color={rootColor}>{title}</b>,
  renderSubcategoryLabel = ({ title }) => <b color={rootColor}>{title}</b>,
}) => {
  const [expandedRootCategoryIds, setExpandedRootCategoryIds] =
    useOptionalLocalStorage<string[]>(storageKey?.expandedRootCategoryIds, []);

  const shouldCollapseRootCategories = !!storageKey?.expandedRootCategoryIds;

  if (!categories.length) {
    return <Text>{noCategoriesMessage ?? "No categories remaining."}</Text>;
  }

  const getAllNonDisabledLeafIds = (category: Category): string[] => {
    const children = (category.subcategories ?? []).filter(
      ({ id }) => !disabledCategoryIds.includes(id),
    );

    if (!children.length) {
      return [category.id];
    }

    return children.flatMap(getAllNonDisabledLeafIds);
  };

  const getCategoryState = (category: Category): CheckedState => {
    const children = (category.subcategories ?? []).filter(
      ({ id }) => !disabledCategoryIds.includes(id),
    );

    if (!children.length) {
      return checkedCategoryIds.includes(category.id);
    }

    const childStates = children.map(getCategoryState);
    const allChecked = childStates.every(s => s === true);
    const noneChecked = childStates.every(s => s === false);

    if (allChecked) return true;
    if (noneChecked) return false;
    return "indeterminate";
  };

  const onCategoryChange = (category: Category, checked: CheckedState) => {
    const leafIds = getAllNonDisabledLeafIds(category);

    let newChecked = checkedCategoryIds.filter(id => !leafIds.includes(id));

    if (checked === true) {
      newChecked = [...newChecked, ...leafIds];
    }

    setCheckedIds(newChecked);
  };

  return (
    <Box>
      <Stack align="flex-start" gap={3} pb="10px">
        {categories.map(category => {
          const isDisabled =
            disableAllCategories || disabledCategoryIds.includes(category.id);

          const checked = getCategoryState(category);

          const hasSubcategories = Boolean(category.subcategories?.length);
          const useCollapsible =
            shouldCollapseRootCategories && hasSubcategories;

          const rootLabel = renderRootCategoryLabel(category);
          const rootCheckbox = (
            <Checkbox
              checked={checked}
              disabled={isDisabled}
              onCheckedChange={({ checked }) =>
                onCategoryChange(category, checked)
              }
              color={rootColor}
            >
              {rootLabel}
            </Checkbox>
          );

          const subcategoryItems = hasSubcategories
            ? category.subcategories?.map(category => {
                const showSubcategories =
                  (category.subcategories ?? []).length > 0;

                const isDisabled =
                  disableAllCategories ||
                  disabledCategoryIds.includes(category.id);

                const checked = getCategoryState(category);

                if (showSubcategories) {
                  return (
                    <Collapsible.Root key={category.id} position="relative">
                      <Flex gap={2} alignItems="center">
                        <Dot />

                        <Collapsible.Trigger
                          bg="#fff"
                          h="20px"
                          w="20px"
                          border="1px solid"
                          borderColor="primary"
                          borderRadius="0.125rem"
                        >
                          <Collapsible.Indicator
                            display="flex"
                            alignContent="center"
                            transition="transform 0.2s ease"
                            _open={{ transform: "rotate(90deg)" }}
                          >
                            <ChevronRightIcon
                              height={18}
                              width={18}
                              color="var(--chakra-colors-primary)"
                            />
                          </Collapsible.Indicator>
                        </Collapsible.Trigger>

                        <Checkbox
                          checked={checked}
                          disabled={isDisabled}
                          onCheckedChange={({ checked }) =>
                            onCategoryChange(category, checked)
                          }
                          color="color-mix(in srgb, var(--chakra-colors-link), black 35%)"
                        >
                          {renderSubcategoryLabel(category)}
                        </Checkbox>
                      </Flex>

                      <Collapsible.Content
                        // avoids drop shadow being cut off from result tags
                        pb="1px"
                      >
                        <Stack mt={2} gap={2}>
                          {supportsSubSubcategorySelection &&
                            category.subcategories?.map(category => {
                              const isDisabled =
                                disableAllCategories ||
                                disabledCategoryIds.includes(category.id);

                              const checked = getCategoryState(category);

                              return (
                                <Flex
                                  key={category.id}
                                  gap={2}
                                  alignItems="center"
                                >
                                  <Dot />
                                  <Dot />
                                  <Dot />
                                  <Checkbox
                                    checked={checked}
                                    disabled={isDisabled}
                                    onCheckedChange={({ checked }) =>
                                      onCategoryChange(category, checked)
                                    }
                                    color="link"
                                  >
                                    {renderSubcategoryLabel(category)}
                                  </Checkbox>
                                </Flex>
                              );
                            })}
                        </Stack>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  );
                }

                return (
                  <Flex key={category.id} gap={2} alignItems="center">
                    <Dot />

                    {supportsSubSubcategorySelection && <Dot />}

                    <Checkbox
                      checked={checked}
                      disabled={isDisabled}
                      onCheckedChange={({ checked }) =>
                        onCategoryChange(category, checked)
                      }
                      color="color-mix(in srgb, var(--chakra-colors-link), black 35%)"
                    >
                      {renderSubcategoryLabel(category)}
                    </Checkbox>
                  </Flex>
                );
              })
            : null;

          if (useCollapsible) {
            const isOpen = expandedRootCategoryIds.includes(category.id);

            const onOpenChange = ({ open }: { open: boolean }) => {
              const current = expandedRootCategoryIds ?? [];

              if (open) {
                setExpandedRootCategoryIds([...current, category.id]);
              } else {
                setExpandedRootCategoryIds(
                  current.filter(id => id !== category.id),
                );
              }
            };

            return (
              <Collapsible.Root
                key={category.id}
                defaultOpen={false}
                open={isOpen}
                onOpenChange={onOpenChange}
              >
                <Flex alignItems="center" gap={2}>
                  <Collapsible.Trigger
                    bg="#fff"
                    h="20px"
                    w="20px"
                    border="1px solid"
                    borderColor="primary"
                    borderRadius="0.125rem"
                  >
                    <Collapsible.Indicator
                      display="flex"
                      alignContent="center"
                      transition="transform 0.2s ease"
                      _open={{ transform: "rotate(90deg)" }}
                    >
                      <ChevronRightIcon
                        height={18}
                        width={18}
                        color="var(--chakra-colors-primary)"
                      />
                    </Collapsible.Indicator>
                  </Collapsible.Trigger>

                  {onlyAllowLeafNodeSelection ? rootLabel : rootCheckbox}
                </Flex>

                <Collapsible.Content pb="1px">
                  <Stack mt={2} gap={2}>
                    {subcategoryItems}
                  </Stack>
                </Collapsible.Content>
              </Collapsible.Root>
            );
          }

          return (
            <Stack key={category.id} align="flex-start">
              {onlyAllowLeafNodeSelection ? rootLabel : rootCheckbox}
              {subcategoryItems && <Stack gap={2}>{subcategoryItems}</Stack>}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default CategorySelection;
